/**
 * X Ads — the frame every X Ads screen sits in: page header, the connection in use
 * (switchable when the company has several), the module's tabs, the "Reconnect X" prompt
 * that appears when X refuses the saved tokens, and the "choose an ads account" card for a
 * connection that has none picked yet.
 */
import { ReactNode, useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ArrowRight, ArrowUpRight, BarChart3, FlaskConical, Layers, Link2, Megaphone, RefreshCw, Rocket, ShieldCheck, Wrench } from 'lucide-react';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { XSelect } from './XSelect';
import { onReconnectRequired } from '@/services/x-ads.service';
import { isUsable, useXConnection } from '@/lib/x-ads/connection';
import { XA_ACCOUNT, XA_CREATIVES, XA_HOME, XA_NEW, XA_REPORTING, XA_TOOLS } from '@/lib/x-ads/routes';
import './x-ads.css';

export type XSection = 'Campaigns' | 'New campaign' | 'Creatives' | 'Tools' | 'Reporting' | 'Account';

const NAV = [
  { label: 'Campaigns', to: XA_HOME, Icon: Megaphone, end: true },
  { label: 'New campaign', to: XA_NEW, Icon: Rocket, end: false },
  { label: 'Creatives', to: XA_CREATIVES, Icon: Layers, end: false },
  { label: 'Tools', to: XA_TOOLS, Icon: Wrench, end: false },
  { label: 'Reporting', to: XA_REPORTING, Icon: BarChart3, end: false },
  { label: 'Account', to: XA_ACCOUNT, Icon: Link2, end: false },
];

/** The X logo. */
export const XGlyph = ({ size = 18, className }: { size?: number; className?: string }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} aria-hidden className={className} fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

export function XAdsNotice({ title, children, error = false }: { title: string; children?: ReactNode; error?: boolean }) {
  return <div role={error ? 'alert' : 'status'} className={`xa-notice ${error ? 'is-error' : ''}`}><strong>{title}</strong>{children && <div>{children}</div>}</div>;
}

/** Shown on any screen once a request answered X_RECONNECT_REQUIRED. */
function ReconnectPrompt() {
  const [message, setMessage] = useState<string | null>(null);
  useEffect(() => onReconnectRequired(setMessage), []);
  if (!message) return null;
  return <XAdsNotice title="Reconnect X to continue" error>
    {message}{' '}
    <Link to={XA_ACCOUNT} className="font-semibold underline" onClick={() => setMessage(null)}>Reconnect X</Link>
  </XAdsNotice>;
}

/** Which connection the screens use, when the company has more than one with an ads account. */
function ConnectionSwitcher() {
  const { accounts, account, setActive } = useXConnection();
  const usable = (accounts ?? []).filter(isUsable);
  if (!account) return null;
  const name = (a: typeof account) => `@${a.screenName || a.xUserId}${a.adsAccountName ? ` · ${a.adsAccountName}` : ''}${a.sandbox ? ' (sandbox)' : ''}`;
  return <div className="flex items-center gap-2">
    {account.sandbox && <span className="xa-chip !bg-warning-soft !text-warning"><FlaskConical size={12} />Sandbox</span>}
    {account.currency && <span className="xa-chip">{account.currency}</span>}
    {usable.length > 1
      ? <XSelect className="w-60" value={account.accountUid} onChange={(v) => v && setActive(v)} options={usable.map((a) => ({ value: a.accountUid, label: name(a) }))} />
      : <span className="hidden text-xs text-text-muted sm:inline">{name(account)}</span>}
  </div>;
}

/** A connection exists but no ads account is picked: the screens have nothing to work on. */
function ChooseAccountCard() {
  return <div className="mx-auto flex min-h-[50vh] max-w-2xl items-center py-6">
    <Card className="w-full p-8 text-center sm:p-10">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-text-primary text-surface shadow-soft"><XGlyph size={28} /></div>
      <h1 className="mt-5 font-display text-[22px] font-bold tracking-tight text-text-primary">Choose your X ads account</h1>
      <p className="mx-auto mt-2 max-w-md text-[14px] leading-relaxed text-text-muted">Your X login is connected. Pick the ads account to work on, and everything below works from here.</p>
      <div className="mt-8 flex flex-col items-center gap-3">
        <Link to={XA_ACCOUNT}><Button iconRight={<ArrowRight size={15} />}>Choose ads account</Button></Link>
        <p className="flex items-center gap-1.5 text-[12px] text-text-muted"><ShieldCheck size={13} />You need a role on the ads account at ads.x.com.</p>
      </div>
    </Card>
  </div>;
}

export function XAdsShell({ section, description, actions, children }: {
  section: XSection; description: string; actions?: ReactNode; children: ReactNode;
}) {
  const { pathname } = useLocation();
  const { accounts, loading, error } = useXConnection();
  const onAccountPage = pathname.startsWith(XA_ACCOUNT);
  const needsAccount = !onAccountPage && !loading && !error && !!accounts?.length && !accounts.some(isUsable);
  return <div className="xa-module min-h-full">
    <Topbar title={`X Ads · ${section}`} description={description} breadcrumb={`X Ads / ${section}`} autoBack={false}
      back={<span className="xa-brand"><XGlyph size={20} /></span>}
      right={<>
        <ConnectionSwitcher />
        <a href="https://ads.x.com/" target="_blank" rel="noreferrer" className="xa-external">Open X Ads <ArrowUpRight size={14} /></a>
      </>} />
    <div className="xa-page">
      <nav className="xa-nav" aria-label="X Ads workspace">
        {NAV.map(({ label, to, Icon, end }) => <NavLink key={to} to={to} end={end} className={({ isActive }) => `xa-nav-link ${isActive ? 'is-active' : ''}`}><Icon size={16} />{label}</NavLink>)}
        {actions && <div className="xa-nav-actions">{actions}</div>}
      </nav>
      <ReconnectPrompt />
      {needsAccount ? <ChooseAccountCard /> : children}
    </div>
  </div>;
}

/** A small "retry" line for inline load errors. */
export function RetryLine({ message, onRetry }: { message: string; onRetry: () => void }) {
  return <XAdsNotice title="Something went wrong" error>
    {message}{' '}
    <Button variant="ghost" size="sm" className="ml-1 h-7" iconLeft={<RefreshCw size={13} />} onClick={onRetry}>Try again</Button>
  </XAdsNotice>;
}
